import React, { useState, useRef } from 'react';
import { FiTrash2, FiPlus, FiLogOut, FiEye, FiEyeOff, FiEdit2, FiUpload, FiRefreshCw } from 'react-icons/fi';
import { useData } from '../context/DataContext';
import dataStore from '../utils/dataStore';

const GITHUB_USER = 'thewickedengineer';

const ADMIN_USER = process.env.REACT_APP_ADMIN_USER || 'admin';
const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || 'wickedengineer';

const CATEGORIES = ['AI', 'Backend', 'Frontend', 'System Design', 'Productivity', 'Engineering Culture', 'DevOps'];
const ICONS = ['server', 'database', 'chart', 'shield'];

const emptyBlog = {
  title: '',
  excerpt: '',
  content: '',
  category: 'AI',
  date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
  readTime: '5 min read',
  tags: '',
  cover: '',
};

const emptyProject = {
  title: '',
  description: '',
  tech: '',
  github: '',
  demo: '',
  icon: 'server',
};

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  const handle = (e) => {
    e.preventDefault();
    if (form.username === ADMIN_USER && form.password === ADMIN_PASS) {
      sessionStorage.setItem('twe-admin-auth', 'true');
      onLogin();
    } else {
      setError('Invalid credentials. Please try again.');
    }
  };

  return (
    <div className="admin-login-wrap">
      <div className="admin-login-card">
        <div className="admin-login-brand">
          <span className="admin-brand-logo">twe</span>
          <span className="admin-brand-label">Admin</span>
        </div>
        <h1>Sign in</h1>
        <p className="admin-login-sub">The Wicked Engineer — Content Management</p>
        <form onSubmit={handle} className="admin-login-form">
          <div className="admin-field">
            <label>Username</label>
            <input
              type="text"
              autoComplete="username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
            />
          </div>
          <div className="admin-field">
            <label>Password</label>
            <div className="admin-pass-wrap">
              <input
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
              <button type="button" className="admin-pass-toggle" onClick={() => setShowPass(!showPass)}>
                {showPass ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>
          {error && <p className="admin-error">{error}</p>}
          <button type="submit" className="admin-btn-primary admin-btn-full">Sign in</button>
        </form>
      </div>
    </div>
  );
}

// ── Image Upload Field ────────────────────────────────────────────────────────
function ImageUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [urlInput, setUrlInput] = useState(
    value && value.startsWith('http') ? value : ''
  );

  React.useEffect(() => {
    if (value && value.startsWith('http')) setUrlInput(value);
    else if (!value) setUrlInput('');
  }, [value]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => { onChange(ev.target.result); setUrlInput(''); };
    reader.readAsDataURL(file);
  };

  const applyUrl = () => {
    const trimmed = urlInput.trim();
    if (trimmed) onChange(trimmed);
  };

  return (
    <div className="admin-image-upload">
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
      <div className="admin-image-input-row">
        <button type="button" className="admin-btn-ghost admin-btn-sm" onClick={() => inputRef.current.click()}>
          <FiUpload /> {value ? 'Change' : 'Upload'}
        </button>
        <span className="admin-upload-or">or paste URL</span>
        <input
          type="url"
          className="admin-url-field"
          placeholder="https://..."
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onBlur={applyUrl}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyUrl())}
        />
      </div>
      {value && (
        <div className="admin-image-preview">
          <img src={value} alt="Cover preview" />
          <button type="button" className="admin-image-remove" onClick={() => { onChange(''); setUrlInput(''); }}>×</button>
        </div>
      )}
    </div>
  );
}

// ── Blog Form ─────────────────────────────────────────────────────────────────
function BlogForm({ initialData, isEditing, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    initialData
      ? { ...initialData, tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : initialData.tags }
      : emptyBlog
  );

  const handle = (e) => {
    e.preventDefault();
    const id = isEditing
      ? form.id
      : form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    onSubmit({
      ...form,
      id,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    });
  };

  return (
    <form className="admin-form" onSubmit={handle}>
      <div className="admin-form-grid">
        <div className="admin-field admin-field-full">
          <label>Title *</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div className="admin-field admin-field-full">
          <label>Excerpt *</label>
          <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required />
        </div>
        <div className="admin-field">
          <label>Category</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="admin-field">
          <label>Read Time</label>
          <input value={form.readTime} onChange={(e) => setForm({ ...form, readTime: e.target.value })} placeholder="5 min read" />
        </div>
        <div className="admin-field">
          <label>Date</label>
          <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="admin-field">
          <label>Tags (comma separated)</label>
          <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="ai, backend, tools" />
        </div>
        <div className="admin-field admin-field-full">
          <label>Cover Image</label>
          <ImageUpload value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} />
        </div>
        <div className="admin-field admin-field-full">
          <label>Content (## H2 · ### H3 · **bold** · `code` · - list · 1. ordered · ![alt](url) image · [text](url) link)</label>
          <textarea
            rows={14}
            className="admin-content-area"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            required
          />
        </div>
      </div>
      <div className="admin-form-actions">
        <button type="submit" className="admin-btn-primary">
          {isEditing ? <><FiEdit2 /> Update Blog</> : <><FiPlus /> Publish Blog</>}
        </button>
        <button type="button" className="admin-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── Project Form ──────────────────────────────────────────────────────────────
function ProjectForm({ initialData, isEditing, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    initialData
      ? { ...initialData, tech: Array.isArray(initialData.tech) ? initialData.tech.join(', ') : initialData.tech }
      : emptyProject
  );

  const handle = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      id: isEditing ? form.id : Date.now(),
      tech: form.tech.split(',').map((t) => t.trim()).filter(Boolean),
      demo: form.demo || null,
    });
  };

  return (
    <form className="admin-form" onSubmit={handle}>
      <div className="admin-form-grid">
        <div className="admin-field admin-field-full">
          <label>Title *</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div className="admin-field admin-field-full">
          <label>Description *</label>
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
        </div>
        <div className="admin-field">
          <label>Tech Stack (comma separated)</label>
          <input value={form.tech} onChange={(e) => setForm({ ...form, tech: e.target.value })} placeholder="React, Node.js, Redis" />
        </div>
        <div className="admin-field">
          <label>Icon</label>
          <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
            {ICONS.map((i) => <option key={i}>{i}</option>)}
          </select>
        </div>
        <div className="admin-field">
          <label>GitHub URL</label>
          <input type="url" value={form.github} onChange={(e) => setForm({ ...form, github: e.target.value })} placeholder="https://github.com/..." />
        </div>
        <div className="admin-field">
          <label>Demo URL (optional)</label>
          <input type="url" value={form.demo} onChange={(e) => setForm({ ...form, demo: e.target.value })} placeholder="https://..." />
        </div>
      </div>
      <div className="admin-form-actions">
        <button type="submit" className="admin-btn-primary">
          {isEditing ? <><FiEdit2 /> Update Project</> : <><FiPlus /> Add Project</>}
        </button>
        <button type="button" className="admin-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ onLogout }) {
  const { blogs, projects, addBlog, updateBlog, deleteBlog, addProject, addProjects, updateProject, deleteProject } = useData();
  const [tab, setTab] = useState('blogs');

  // Blog edit/add state
  const [blogPanel, setBlogPanel] = useState(null); // null | { mode: 'add' | 'edit', data?: blog }

  // Project edit/add state
  const [projectPanel, setProjectPanel] = useState(null); // null | { mode: 'add' | 'edit', data?: project }

  // GitHub sync state
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const subscribers = dataStore.getSubscribers();

  const handleGithubSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await fetch(
        `https://api.github.com/users/${GITHUB_USER}/repos?sort=updated&per_page=30`
      );
      if (!res.ok) throw new Error('GitHub API error');
      const repos = await res.json();

      const existingUrls = new Set(projects.map((p) => p.github).filter(Boolean));
      const newRepos = repos
        .filter((r) => !existingUrls.has(r.html_url))
        .map((r) => ({
          id: r.id,
          title: r.name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          description: r.description || 'No description provided.',
          tech: r.language ? [r.language] : [],
          github: r.html_url,
          demo: r.homepage || null,
          icon: 'server',
        }));

      if (newRepos.length > 0) {
        addProjects(newRepos);
        setSyncMsg(`Synced ${newRepos.length} new repo${newRepos.length > 1 ? 's' : ''} from GitHub`);
      } else {
        setSyncMsg('Already up to date');
      }
    } catch {
      setSyncMsg('Sync failed — check connection or GitHub rate limit');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(''), 5000);
    }
  };

  const handleBlogSubmit = (data) => {
    if (blogPanel.mode === 'edit') {
      updateBlog(data.id, data);
    } else {
      addBlog(data);
    }
    setBlogPanel(null);
  };

  const handleProjectSubmit = (data) => {
    if (projectPanel.mode === 'edit') {
      updateProject(data.id, data);
    } else {
      addProject(data);
    }
    setProjectPanel(null);
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-brand">
          <span className="admin-brand-logo">twe</span>
          <span className="admin-header-title">Admin Panel</span>
        </div>
        <button className="admin-btn-ghost admin-btn-sm" onClick={onLogout}>
          <FiLogOut /> Logout
        </button>
      </header>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'blogs' ? 'active' : ''}`} onClick={() => setTab('blogs')}>
          Blogs <span className="admin-tab-count">{blogs.length}</span>
        </button>
        <button className={`admin-tab ${tab === 'projects' ? 'active' : ''}`} onClick={() => setTab('projects')}>
          Projects <span className="admin-tab-count">{projects.length}</span>
        </button>
        <button className={`admin-tab ${tab === 'subscribers' ? 'active' : ''}`} onClick={() => setTab('subscribers')}>
          Subscribers <span className="admin-tab-count">{subscribers.length}</span>
        </button>
      </div>

      <div className="admin-content">

        {/* ── Blogs Tab ── */}
        {tab === 'blogs' && (
          <div>
            <div className="admin-section-header">
              <h2>Blog Posts</h2>
              <button
                className="admin-btn-primary admin-btn-sm"
                onClick={() => setBlogPanel(blogPanel ? null : { mode: 'add' })}
              >
                <FiPlus /> {blogPanel?.mode === 'add' ? 'Cancel' : 'New Post'}
              </button>
            </div>

            {blogPanel && (
              <div className="admin-form-wrap">
                <h3>{blogPanel.mode === 'edit' ? 'Edit Blog Post' : 'New Blog Post'}</h3>
                <BlogForm
                  initialData={blogPanel.data}
                  isEditing={blogPanel.mode === 'edit'}
                  onSubmit={handleBlogSubmit}
                  onCancel={() => setBlogPanel(null)}
                />
              </div>
            )}

            <div className="admin-list">
              {blogs.length === 0 && <p className="admin-empty">No blog posts yet.</p>}
              {blogs.map((blog) => (
                <div key={blog.id} className="admin-list-item">
                  {blog.cover && <img src={blog.cover} alt="" className="admin-list-thumb" />}
                  <div className="admin-list-info">
                    <span className="admin-list-label">{blog.category}</span>
                    <strong>{blog.title}</strong>
                    <span className="admin-list-meta">{blog.date} · {blog.readTime}</span>
                  </div>
                  <div className="admin-list-actions">
                    <button
                      className="admin-btn-icon"
                      onClick={() => setBlogPanel({ mode: 'edit', data: blog })}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="admin-btn-icon admin-btn-delete"
                      onClick={() => { if (window.confirm(`Delete "${blog.title}"?`)) deleteBlog(blog.id); }}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Projects Tab ── */}
        {tab === 'projects' && (
          <div>
            <div className="admin-section-header">
              <h2>Projects</h2>
              <div className="admin-section-actions">
                <button
                  className="admin-btn-ghost admin-btn-sm"
                  onClick={handleGithubSync}
                  disabled={syncing}
                  title="Sync repositories from GitHub"
                >
                  <FiRefreshCw className={syncing ? 'spin' : ''} />
                  {syncing ? 'Syncing…' : 'Sync GitHub'}
                </button>
                {syncMsg && <span className="admin-sync-msg">{syncMsg}</span>}
                <button
                  className="admin-btn-primary admin-btn-sm"
                  onClick={() => setProjectPanel(projectPanel ? null : { mode: 'add' })}
                >
                  <FiPlus /> {projectPanel?.mode === 'add' ? 'Cancel' : 'New Project'}
                </button>
              </div>
            </div>

            {projectPanel && (
              <div className="admin-form-wrap">
                <h3>{projectPanel.mode === 'edit' ? 'Edit Project' : 'New Project'}</h3>
                <ProjectForm
                  initialData={projectPanel.data}
                  isEditing={projectPanel.mode === 'edit'}
                  onSubmit={handleProjectSubmit}
                  onCancel={() => setProjectPanel(null)}
                />
              </div>
            )}

            <div className="admin-list">
              {projects.length === 0 && <p className="admin-empty">No projects yet.</p>}
              {projects.map((project) => (
                <div key={project.id} className="admin-list-item">
                  <div className="admin-list-info">
                    <strong>{project.title}</strong>
                    <span className="admin-list-meta">
                      {Array.isArray(project.tech) ? project.tech.join(', ') : project.tech}
                    </span>
                  </div>
                  <div className="admin-list-actions">
                    <button
                      className="admin-btn-icon"
                      onClick={() => setProjectPanel({ mode: 'edit', data: project })}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="admin-btn-icon admin-btn-delete"
                      onClick={() => { if (window.confirm(`Delete "${project.title}"?`)) deleteProject(project.id); }}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Subscribers Tab ── */}
        {tab === 'subscribers' && (
          <div>
            <div className="admin-section-header">
              <h2>Subscribers</h2>
              <span className="admin-list-meta">{subscribers.length} total</span>
            </div>
            <div className="admin-list">
              {subscribers.length === 0 && <p className="admin-empty">No subscribers yet.</p>}
              {subscribers.map((s, i) => (
                <div key={i} className="admin-list-item">
                  <div className="admin-list-info">
                    <strong>{s.email}</strong>
                    <span className="admin-list-meta">{new Date(s.subscribedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────
export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => sessionStorage.getItem('twe-admin-auth') === 'true'
  );

  const handleLogout = () => {
    sessionStorage.removeItem('twe-admin-auth');
    setIsLoggedIn(false);
  };

  return isLoggedIn
    ? <Dashboard onLogout={handleLogout} />
    : <LoginScreen onLogin={() => setIsLoggedIn(true)} />;
}
